<template>
  <div class="about">
    <div class="p-2 ml-2 bg-light gothic fontSizeDot9 border-top border-bottom">
      <router-link :to="{ name: 'tinkHome' }" custom v-slot="{ navigate }">
        <span @click="navigate" @keypress.enter="navigate" class="cursor-pointer" role="link">
          처음
        </span>  
      </router-link>
      &gt;
      <b-dropdown text="Core 프로그램" size="sm" role="menu" toggle-class="pl-2 pr-2 pt-dot1 pb-dot1">
        <b-dropdown-item :to="{ name: 'tinkAbout' }">
          Tinkable 소개
        </b-dropdown-item>
        <b-dropdown-item :to="{ name: 'tinkAboutKit' }">
          IoT 키트
        </b-dropdown-item>
        <b-dropdown-item :to="{ name: 'tinkAboutCore' }" :active="true">
          Core 프로그램
        </b-dropdown-item>
        <b-dropdown-item :to="{ name: 'tinkAboutData' }">
          Data 서비스
        </b-dropdown-item>
        <b-dropdown-item :to="{ name: 'tinkTos' }">
          서비스 약관
        </b-dropdown-item>
        <b-dropdown-item :to="{ name: 'tinkPrivacy' }">
          개인정보 처리방침
        </b-dropdown-item>
      </b-dropdown>
    </div>

    <h1 class="documentTitle" v-if="!presentationMode">
      Tinkable Core 프로그램 소개
    </h1>

    <div class="contentBody">

      <h2><a name="ide">아두이노 IDE ESP8266 개발환경 구축</a></h2>
      <ul>
        <li>
          <b>&rarr;</b>&nbsp;<a href="https://www.arduino.cc" target="_new">https://www.arduino.cc</a>을 방문하여 Software로 이동하여 Arduino IDE를 다운로드 받아서 설치합니다. Windows의 경우 Windows App용 말고 Win 7 and newer 버전으로 다운로드 받는 것이 좋습니다. 다운로드 페이지에서 Just Download를 누르면 기부없이 바로 다운로드 합니다.
        </li>
        <li>
          <b>&rarr;</b>&nbsp;<a href="https://sparks.gogo.co.nz/ch340.html">https://sparks.gogo.co.nz/ch340.html</a>에서 CH340 Driver를 다운로드 받아서 실행한 후에 INSTALL 버튼을 눌러서 설치합니다.
        </li>
        <li>
          Arduino IDE 프로그램을 실행하여 파일/환경설정에서 추가적인 보드매니저 URL에 <tt>https://dl.espressif.com/dl/package_esp32_index.json, http://arduino.esp8266.com/stable/package_esp8266com_index.json</tt>를 입력합니다.
        </li>
        <li>
          툴/보드/보드매니저에서 ESP8266으로 검색하여 <tt>esp8266 by ESP8266 Community</tt> 보드에서 버전을 <b style="color: red;">2.7.4</b>로 선택하여 설치합니다. 현재 팅커블Core프로그램은 ESP8266 라이브러리를 2.7.4 버전을 사용하고 있고 아래의 tinkUpdate.bat 프로그램도 해당 경로의 upload.py 프로그램을 사용합니다. 다른 버전을 설치하면 팅커블Core프로그램의 컴파일 시에 문제가 생길 수도 있고 tinkUpdate.bat 프로그램이 정상 동작하지 않습니다. 다른 버전을 설치해야하는 상황이라면 tinkUpdate.bat을 텍스트 에디터에서 열여서 upload.py 프로그램의 경로를 설치한 버전에 맞게 수정해야 합니다.
        </li>
        <li>
          툴/보드/<tt>ESP8266 Boards/Lolin(WEMOS) D1 R2&amp;mini</tt>를 선택합니다. 팅커블 IoT 키트를 microUSB 케이블로 PC와 연결하면 툴 메뉴의 포트 항목에 COM5와 같이 연결된 시리얼 이름이 표시됩니다.
        </li>
      </ul>
      <h2><a name="ide">팅커블IoT키트 프로그램 업그레이드</a></h2>
      <p>
        컴퓨터에 위의 설명에 따라서 아두이노 IDE ESP8266 개발환경을 구축합니다. 팅커블 IoT 키트를 microUSB 케이블로 PC와 연결하면 아두이노 IDE 프로그램의 툴 메뉴의 포트 항목에 COM5와 같이 연결된 시리얼 이름을 확인합니다. 그리고, <b>아두이노 IDE 프로그램을 종료합니다(프로그램 업그레이드와 아두이노 IDE를 동시에 실행하면 명령 프롬프트에서 프로그램 업로드시에 시리얼 쓰기 에러가 날 수 있습니다)</b>.
      </p>
      <p>
        다음 파일을 다운로드 받아서 아두이노 IDE가 설치된 <q><tt>C:\Program Files (x86)\Arduino</tt></q> 아래로 복사합니다.
      </p>
      <ul>
        <li>
          팅커블Core프로그램 1.7버전 바이너리 파일: <b>&rarr;</b>&nbsp;<a href="https://storage.googleapis.com/imgcdn.tinkable.net/bin/tink170.bin" target="_new">tink170.bin</a>
        </li>
        <li>
          업데이트 배치파일: <b>&rarr;</b>&nbsp;<a href="https://storage.googleapis.com/imgcdn.tinkable.net/util/tinkUpdate.bat" target="_new">tinkUpdate.bat</a>
        </li>
      </ul>
      <p>
        <b>아두이노 IDE 프로그램을 종료한 상태에서 명령 프롬프트를  실행</b>한 다음에 아두이노 IDE가 설치된 <q><tt>C:\Program Files (x86)\Arduino</tt></q>로 이동합니다. 그리고, 다음과 같이 실행하면 팅커블IoT키트에 팅커블Core 프로그램 1.7 버전을 업로드합니다. 여기서 [시리얼이름]은 팅커블IoT키트를 microUSB케이블로 PC와 연결했을 때 아두이노IDE의 툴 메뉴의 포트 항목에 표시됩니다. 그리고, 리눅스, 매킨토시 환경에서도 tinkUpdate.bat 파일의 내용을 참고로 python 프로그램, upload.py 프로그램의 위치를 찾아서 동일한 코드를 실행하면 됩니다.
      </p>
      <p class="p-2 border bg-secondary-lighter">
        <tt>tinkUpdate.bat <i>[시리얼이름]</i> tink170.bin</tt>
      </p>
      <p style="color: red;"><br/><br/><br/>아래 내용은 작성 중에 있습니다.</p>
      <h2><a name="intro">개요</a></h2>
      <!--div class="extra">

      demo01 소스코드

    </div-->
      <p>Core 프로그램...</p>


      <h2><a name="hardware">사용 라이브러리</a></h2>
      <ul>
        <li>ESP8266 Wemos D1 R2: </li>
        <li>아두이노 우노, 메가: </li>
        <li>ESP32: ?</li>
        <li>Raspberry Pi: ?</li>
      </ul>

      <h2><a name="hardware">지원 하드웨어</a></h2>
      <ul>
        <li>ESP8266 Wemos D1 R2: </li>
        <li>아두이노 우노, 메가: </li>
        <li>ESP32: ?</li>
        <li>Raspberry Pi: ?</li>
      </ul>

      <h2><a name="history">개발 기록</a></h2>
      <h3>V1.7</h3>
      <ul>
        <li></li>
      </ul>
      <h3>V1.5</h3>
      <ul>
        <li></li>
      </ul>
      <h3>V1.3</h3>
      <ul>
        <li></li>
      </ul>
      <h3>V1.0</h3>
      <ul>
        <li></li>
      </ul>
    </div>
  </div>
</template>
<script>
  import { mapState } from "vuex"
  import { mapSetter } from "@/plugins/vuexUtils.js"
  export default {
    name: "tinkAbout",
    computed: {
      ...mapSetter(
        mapState({
          presentationMode:  state => state.presentationMode,
        })
      )
    },
    mounted() {
      window.document.body.scrollIntoView(true);
    },
  }
</script>
